
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Filter, 
  Search, 
  Plus, 
  RefreshCw,
  TrendingUp,
  Clock,
  Activity,
  Globe,
  Star,
  UserPlus,
  Bookmark,
  Sparkles,
  Users,
  MessageSquare,
  Lightbulb,
  Calendar,
  Award,
  Flame,
  Share2,
  BarChart2,
  Crown,
  Zap
} from "lucide-react";
import { PostCard } from "@/components/posts/PostCard";
import { CreatePostForm } from "@/components/posts/CreatePostForm";
import { usePostsStore } from "@/store/postsStore";
import { Post } from "@/types/post";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { debounce, formatDate, getInitials, shuffleArray } from '@/lib/utils';
import { CommunityBackground } from '@/components/posts/CommunityBackground';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const trendingTags = ["Design", "Development", "AI", "Freelancing", "Remote Work", "UI/UX", "Marketing", "Web3", "Blockchain", "Productivity"];

const activeUsers = [
  { id: "1", name: "Emma Wilson", role: "UI Designer", avatar: "", status: "online" },
  { id: "2", name: "James Rodriguez", role: "Full-Stack Developer", avatar: "", status: "online" },
  { id: "3", name: "Aisha Khan", role: "Product Manager", avatar: "", status: "away" },
  { id: "4", name: "Takashi Yamamoto", role: "Data Scientist", avatar: "", status: "online" },
  { id: "5", name: "Sofia Martinez", role: "Marketing Specialist", avatar: "", status: "busy" },
];

const featuredEvents = [
  { 
    id: "1", 
    title: "Design Systems Workshop", 
    date: "2023-07-15T14:30:00", 
    participants: 42,
    type: "Webinar",
    category: "design"
  },
  { 
    id: "2", 
    title: "Freelancer Networking", 
    date: "2023-07-22T18:00:00", 
    participants: 86,
    type: "Meetup",
    category: "networking"
  },
  { 
    id: "3", 
    title: "AI in Creative Industries", 
    date: "2023-07-28T16:00:00", 
    participants: 125,
    type: "Panel",
    category: "technology"
  },
  { 
    id: "4", 
    title: "Remote Work Best Practices", 
    date: "2023-08-05T15:00:00", 
    participants: 73,
    type: "Workshop",
    category: "productivity"
  },
];

const communityStats = [
  { label: "Members", value: "12,543", icon: Users, color: "text-sky-500", bgColor: "bg-sky-50" },
  { label: "Posts", value: "32,891", icon: MessageSquare, color: "text-emerald-500", bgColor: "bg-emerald-50" },
  { label: "Ideas Shared", value: "9,327", icon: Lightbulb, color: "text-amber-500", bgColor: "bg-amber-50" },
  { label: "Events", value: "1,248", icon: Calendar, color: "text-purple-500", bgColor: "bg-purple-50" },
];

// Top community contributors
const topContributors = [
  { name: "Maya Johnson", posts: 48, likes: 356, avatar: "" },
  { name: "Alex Chen", posts: 36, likes: 289, avatar: "" },
  { name: "Olivia Williams", posts: 29, likes: 245, avatar: "" },
];

// Animation variants for staggered animations
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12
    }
  }
};

const Posts = () => {
  const { posts } = usePostsStore();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredPosts, setFilteredPosts] = useState<Post[]>(posts);
  const [sortBy, setSortBy] = useState("latest");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [isCreatePostOpen, setIsCreatePostOpen] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);

  // Filter and sort posts when data changes
  useEffect(() => {
    // Initial filter based on active tab
    let result = posts;

    if (activeTab === "bookmarked") {
      // In a real app, this would filter based on user's bookmarked posts
      result = result.filter(post => post.likes > 2);
    } else if (activeTab === "popular") {
      result = result.filter(post => post.likes > 0);
    }
    
    // Filter by selected tag if any
    if (selectedTag) {
      result = result.filter(post => 
        post.tags.some(tag => tag.toLowerCase() === selectedTag.toLowerCase())
      );
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(post => 
        post.title.toLowerCase().includes(term) || 
        post.content.toLowerCase().includes(term) ||
        post.tags.some(tag => tag.toLowerCase().includes(term)) ||
        post.authorName.toLowerCase().includes(term)
      );
    }
    
    // Sort posts
    switch (sortBy) {
      case "latest":
        result = [...result].sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        break;
      case "popular":
        result = [...result].sort((a, b) => b.likes - a.likes);
        break;
      case "comments":
        result = [...result].sort((a, b) => b.comments.length - a.comments.length);
        break;
      case "random":
        result = shuffleArray(result);
        break;
    }
    
    setFilteredPosts(result);
  }, [posts, searchTerm, sortBy, activeTab, selectedTag]);

  // Debounced search handler
  const handleSearch = debounce((value: string) => {
    setSearchTerm(value);
  }, 300);

  const handleViewDetails = (postId: string) => {
    setExpandedPostId(expandedPostId === postId ? null : postId);
  };

  const handleTagClick = (tag: string) => {
    setSelectedTag(selectedTag === tag ? null : tag);
  };

  return (
    <div className="relative min-h-screen pb-16">
      {/* Background */}
      <CommunityBackground />
      
      <div className="container mx-auto pt-8 pb-24 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10"
        >
          {/* Community Header with Stats */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
            <div>
              <div className="mb-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-sky-100 to-emerald-100 text-sky-700 border border-sky-200/50">
                  <Users className="w-3 h-3 mr-1" />
                  Community
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-emerald-600">
                Community Hub
              </h1>
              <p className="text-gray-600 max-w-2xl">Connect with fellow professionals, share insights, and collaborate on the next big idea. Join the conversation and grow your network.</p>
            </div>
            <div className="flex items-center gap-3">
              <Dialog open={isCreatePostOpen} onOpenChange={setIsCreatePostOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 shadow-md hover:shadow-lg transition-all"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Post
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogTitle>Create a New Post</DialogTitle>
                  <CreatePostForm onSuccess={() => setIsCreatePostOpen(false)} />
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Highlight Cards - NEW */}
          <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-sky-50 to-blue-50 border-blue-100 shadow-sm hover:shadow-md transition-all">
              <CardHeader className="pb-2">
                <div className="flex items-center mb-2">
                  <div className="p-2 rounded-full bg-sky-100 mr-3">
                    <Flame className="h-5 w-5 text-sky-600" />
                  </div>
                  <CardTitle className="text-lg text-sky-800">Hot Topic</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <h4 className="font-medium text-gray-900 mb-1">The Future of Remote Work</h4>
                <p className="text-sm text-gray-600 mb-3">Join the conversation about hybrid models and digital nomads</p>
                <div className="flex items-center text-sm text-sky-700">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  <span>148 posts this week</span>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-100 shadow-sm hover:shadow-md transition-all">
              <CardHeader className="pb-2">
                <div className="flex items-center mb-2">
                  <div className="p-2 rounded-full bg-emerald-100 mr-3">
                    <Zap className="h-5 w-5 text-emerald-600" />
                  </div>
                  <CardTitle className="text-lg text-emerald-800">Featured Poll</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <h4 className="font-medium text-gray-900 mb-1">What's your preferred work setup?</h4>
                <p className="text-sm text-gray-600 mb-3">Remote (64%) • Hybrid (28%) • Office (8%)</p>
                <div className="flex items-center text-sm text-emerald-700">
                  <BarChart2 className="h-4 w-4 mr-2" />
                  <span>892 votes so far</span>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-100 shadow-sm hover:shadow-md transition-all">
              <CardHeader className="pb-2">
                <div className="flex items-center mb-2">
                  <div className="p-2 rounded-full bg-purple-100 mr-3">
                    <Crown className="h-5 w-5 text-purple-600" />
                  </div>
                  <CardTitle className="text-lg text-purple-800">Community Challenge</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <h4 className="font-medium text-gray-900 mb-1">30 Days of Portfolio Building</h4>
                <p className="text-sm text-gray-600 mb-3">Share your progress and get feedback from peers</p>
                <div className="flex items-center text-sm text-purple-700">
                  <Users className="h-4 w-4 mr-2" />
                  <span>312 participants</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Community Stats - IMPROVED */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
          >
            {communityStats.map((stat, index) => (
              <motion.div 
                key={stat.label}
                variants={itemVariants}
                className="bg-white/80 backdrop-blur-sm rounded-lg border border-blue-100 shadow-sm hover:shadow-md transition-all p-4 text-center"
              >
                <div className="flex justify-center mb-2">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${stat.bgColor}`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
                <div className="font-bold text-2xl">{stat.value}</div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Main Content - Posts */}
            <div className="lg:col-span-8">
              <Card className="mb-6 overflow-hidden border-blue-100 bg-white/90 backdrop-blur-sm shadow-md">
                <CardHeader className="bg-gradient-to-r from-sky-50 to-emerald-50 p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <Tabs 
                      defaultValue="all" 
                      className="w-full" 
                      onValueChange={setActiveTab}
                    >
                      <TabsList className="grid grid-cols-3 mb-4 md:mb-0">
                        <TabsTrigger value="all" className="data-[state=active]:bg-sky-100 data-[state=active]:text-sky-700">
                          <Globe className="w-4 h-4 mr-2" />
                          All Posts
                        </TabsTrigger>
                        <TabsTrigger value="popular" className="data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700">
                          <Star className="w-4 h-4 mr-2" />
                          Popular
                        </TabsTrigger>
                        <TabsTrigger value="bookmarked" className="data-[state=active]:bg-amber-100 data-[state=active]:text-amber-700">
                          <Bookmark className="w-4 h-4 mr-2" />
                          Saved
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row gap-4 items-center mb-6">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                      <Input
                        placeholder="Search posts, topics, or users..."
                        className="pl-10 border-blue-100"
                        onChange={(e) => handleSearch(e.target.value)}
                      />
                    </div>

                    <div className="flex items-center gap-4 w-full md:w-auto">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="border-blue-100 hover:bg-blue-50"
                              onClick={() => setFilterVisible(!filterVisible)}
                            >
                              <Filter className="h-4 w-4 text-sky-500 mr-2" />
                              Filters
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Filter and sort your feed</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      
                      <AnimatePresence>
                        {filterVisible && (
                          <motion.div 
                            initial={{ opacity: 0, width: 0 }}
                            animate={{ opacity: 1, width: "auto" }}
                            exit={{ opacity: 0, width: 0 }}
                            className="flex items-center gap-2 overflow-hidden"
                          >
                            <Select 
                              defaultValue="latest" 
                              onValueChange={value => setSortBy(value)}
                            >
                              <SelectTrigger className="w-full md:w-[140px] border-blue-100">
                                <SelectValue placeholder="Sort by" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="latest">
                                  <div className="flex items-center">
                                    <Clock className="h-4 w-4 mr-2 text-sky-500" />
                                    Latest
                                  </div>
                                </SelectItem>
                                <SelectItem value="popular">
                                  <div className="flex items-center">
                                    <TrendingUp className="h-4 w-4 mr-2 text-emerald-500" />
                                    Most Liked
                                  </div>
                                </SelectItem>
                                <SelectItem value="comments">
                                  <div className="flex items-center">
                                    <Activity className="h-4 w-4 mr-2 text-purple-500" />
                                    Most Active
                                  </div>
                                </SelectItem>
                                <SelectItem value="random">
                                  <div className="flex items-center">
                                    <RefreshCw className="h-4 w-4 mr-2 text-amber-500" />
                                    Surprise Me
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  {selectedTag && (
                    <div className="mb-4 flex items-center gap-2">
                      <span className="text-sm text-gray-600">Filtered by tag:</span>
                      <Badge 
                        className="bg-sky-100 text-sky-700 hover:bg-sky-200 cursor-pointer"
                        onClick={() => setSelectedTag(null)}
                      >
                        {selectedTag} ×
                      </Badge>
                    </div>
                  )}

                  <motion.div 
                    className="space-y-6"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {filteredPosts.length > 0 ? (
                      filteredPosts.map(post => (
                        <motion.div key={post.id} variants={itemVariants}>
                          <PostCard 
                            post={post} 
                            isDetailed={expandedPostId === post.id}
                            onViewDetails={handleViewDetails}
                          />
                        </motion.div>
                      ))
                    ) : (
                      <motion.div variants={itemVariants} className="text-center py-12 bg-white/80 backdrop-blur-sm rounded-lg shadow-sm">
                        <div className="w-16 h-16 mx-auto mb-4 bg-sky-50 rounded-full flex items-center justify-center">
                          <MessageSquare className="w-8 h-8 text-sky-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No posts found</h3>
                        <p className="text-gray-600 mb-6">
                          {searchTerm || selectedTag
                            ? "Try adjusting your search term or filters" 
                            : "Be the first to create a post in the community!"}
                        </p>
                        {!searchTerm && !selectedTag && (
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button 
                                className="bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Create New Post
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                              <DialogTitle>Create New Post</DialogTitle>
                              <CreatePostForm onSuccess={() => {}} />
                            </DialogContent>
                          </Dialog>
                        )}
                      </motion.div>
                    )}
                  </motion.div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar - ENHANCED */}
            <div className="lg:col-span-4 space-y-6">
              {/* Top Contributors - NEW */}
              <Card className="border-blue-100 overflow-hidden bg-white/90 backdrop-blur-sm shadow-md">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 p-4">
                  <CardTitle className="text-lg flex items-center">
                    <Award className="w-4 h-4 mr-2 text-amber-500" />
                    Top Contributors
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-4">
                    {topContributors.map((contributor, index) => (
                      <div key={index} className="flex items-center">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="relative">
                            <Avatar className="h-10 w-10 border-2 border-amber-100">
                              <AvatarFallback className="bg-gradient-to-br from-amber-100 to-orange-100 text-amber-700">
                                {getInitials(contributor.name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-100 border-2 border-white flex items-center justify-center">
                              <span className="text-[10px] font-bold text-amber-700">{index + 1}</span>
                            </div>
                          </div>
                          <div>
                            <div className="font-medium text-sm">{contributor.name}</div>
                            <div className="text-xs text-gray-500 flex items-center gap-2">
                              <span>{contributor.posts} posts</span>
                              <span>•</span>
                              <span>{contributor.likes} likes</span>
                            </div>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <Share2 className="h-4 w-4 text-amber-500" />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" className="w-full mt-3 border-amber-100 text-amber-600 hover:bg-amber-50">
                      View All Contributors
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Active Users */}
              <Card className="border-blue-100 overflow-hidden bg-white/90 backdrop-blur-sm shadow-md">
                <CardHeader className="bg-gradient-to-r from-sky-50 to-blue-50 p-4">
                  <CardTitle className="text-lg flex items-center">
                    <Users className="w-4 h-4 mr-2 text-sky-500" />
                    Online Community Members
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {activeUsers.map(user => (
                      <div key={user.id} className="flex items-center gap-3 p-2 hover:bg-sky-50 rounded-md transition-colors">
                        <Avatar className="h-10 w-10 border-2 border-sky-100 relative">
                          <AvatarFallback className="bg-gradient-to-br from-sky-100 to-blue-100 text-sky-700">
                            {getInitials(user.name)}
                          </AvatarFallback>
                          <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                            user.status === 'online' ? 'bg-emerald-500' : 
                            user.status === 'away' ? 'bg-amber-500' : 
                            'bg-gray-300'
                          }`} />
                        </Avatar>
                        <div>
                          <div className="font-medium text-sm">{user.name}</div>
                          <div className="text-xs text-gray-500">{user.role}</div>
                        </div>
                        <Button variant="ghost" size="sm" className="ml-auto h-8 w-8 p-0">
                          <UserPlus className="h-4 w-4 text-sky-500" />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" className="w-full mt-3 border-sky-100 text-sky-600 hover:bg-sky-50">
                      View All Members
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Trending Topics */}
              <Card className="border-blue-100 overflow-hidden bg-white/90 backdrop-blur-sm shadow-md">
                <CardHeader className="bg-gradient-to-r from-emerald-50 to-cyan-50 p-4">
                  <CardTitle className="text-lg flex items-center">
                    <TrendingUp className="w-4 h-4 mr-2 text-emerald-500" />
                    Trending Topics
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {trendingTags.map((tag, index) => (
                      <Badge 
                        key={index} 
                        variant="outline" 
                        className={`
                          cursor-pointer transition-colors
                          ${selectedTag === tag ? 'bg-sky-100 text-sky-700 border-sky-300' : 'hover:bg-sky-50 border-sky-100'}
                        `}
                        onClick={() => handleTagClick(tag)}
                      >
                        #{tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Featured Events */}
              <Card className="border-blue-100 overflow-hidden bg-white/90 backdrop-blur-sm shadow-md">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 p-4">
                  <CardTitle className="text-lg flex items-center">
                    <Sparkles className="w-4 h-4 mr-2 text-amber-500" />
                    Featured Events
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {featuredEvents.map(event => (
                      <div key={event.id} className="p-3 border border-amber-100 rounded-lg hover:bg-amber-50/50 transition-colors">
                        <div className="flex justify-between items-start mb-1">
                          <h4 className="font-medium text-gray-900">{event.title}</h4>
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            {event.type}
                          </Badge>
                        </div>
                        <div className="text-xs text-gray-500 mb-2">
                          {formatDate(event.date)}
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-xs text-gray-600">
                            <Users className="h-3 w-3 mr-1 text-amber-500" />
                            {event.participants} participants
                          </div>
                          <Badge variant="outline" className="text-xs bg-white">
                            {event.category}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" className="w-full mt-3 border-amber-100 text-amber-600 hover:bg-amber-50">
                      View All Events
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* Community Achievements */}
              <Card className="border-blue-100 overflow-hidden bg-white/90 backdrop-blur-sm shadow-md">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 p-4">
                  <CardTitle className="text-lg flex items-center">
                    <Award className="w-4 h-4 mr-2 text-purple-500" />
                    Community Highlights
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-purple-50 to-white rounded-lg">
                      <div className="bg-purple-100 p-2 rounded-full">
                        <Award className="h-5 w-5 text-purple-500" />
                      </div>
                      <div>
                        <div className="text-sm font-medium">Most Engaged Member</div>
                        <div className="text-xs text-gray-500">Emma Wilson</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-sky-50 to-white rounded-lg">
                      <div className="bg-sky-100 p-2 rounded-full">
                        <MessageSquare className="h-5 w-5 text-sky-500" />
                      </div>
                      <div>
                        <div className="text-sm font-medium">Most Discussed Topic</div>
                        <div className="text-xs text-gray-500">AI Development Trends</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-emerald-50 to-white rounded-lg">
                      <div className="bg-emerald-100 p-2 rounded-full">
                        <TrendingUp className="h-5 w-5 text-emerald-500" />
                      </div>
                      <div>
                        <div className="text-sm font-medium">Fastest Growing Group</div>
                        <div className="text-xs text-gray-500">Remote Work Professionals</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Posts;
